import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Input, Result, Space, Text, Title } from "@app/ui";
import { forgotPasswordSchema } from "@app/core/schemas";
import { api } from "../api/client.js";
import { AuthShell } from "../auth/AuthShell.js";
import { useT } from "../i18n.js";
import { ErrorAlert, useFormErrors } from "../errors/index.js";

/**
 * Forgot-password entry. Collects the email and asks the API for a reset link.
 * The endpoint always succeeds (204) whether or not the email is registered, so
 * the confirmation is deliberately neutral — never confirm that an account
 * exists. Public route.
 */
export function ForgotPasswordPage() {
  const t = useT();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const errs = useFormErrors();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const parsed = errs.validate(forgotPasswordSchema, { email: email.trim() });
    if (!parsed.ok) return;
    setBusy(true);
    void (async () => {
      try {
        await api<void>("/auth/password/forgot", {
          method: "POST",
          body: parsed.data,
          noRetry: true,
        });
        setSent(true);
      } catch (err) {
        errs.handle(err);
      } finally {
        setBusy(false);
      }
    })();
  };

  if (sent) {
    return (
      <AuthShell>
        <Result
          status="success"
          title={t("forgot.sentTitle")}
          subTitle={t("forgot.sentBody")}
          extra={
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t("verify.goLogin")}
            </Button>
          }
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        <Title level={3}>{t("forgot.title")}</Title>
        <Text secondary>{t("forgot.subtitle")}</Text>
        {errs.formError && <ErrorAlert error={errs.formError} />}
        <form onSubmit={onSubmit}>
          <Input
            name="email"
            label={t("common.email")}
            type="email"
            autoComplete="email"
            placeholder={t("login.emailPlaceholder")}
            prefix={<Icon name="mail" size={14} />}
            value={email}
            error={errs.fieldErrors.email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              errs.clearField("email");
            }}
            autoFocus
            required
          />
          <Button variant="primary" block loading={busy} type="submit">
            {t("forgot.submit")}
          </Button>
        </form>
        <div className="auth-back">
          <Button variant="link" onClick={() => navigate("/login")}>
            {t("login.back")}
          </Button>
        </div>
      </Space>
    </AuthShell>
  );
}
